'use client';

import Link from '@eliadmin/core/src/components/link';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchReferences } from '@eliadmin/core/src/api/references/references-api';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ReferenceCard } from './reference-card';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { Pagination } from '../shared/pagination';
import type { ReferenceCategory } from '@eliadmin/core/src/api/references/references-types';

const ALL_CATEGORIES_TAB = 'all';

export const ReferencesList = ({
  locale,
  categories,
  initialReferences,
  listHref,
}: {
  locale: string;
  categories: ReferenceCategory[];
  initialReferences?: Awaited<ReturnType<typeof fetchReferences>>;
  listHref?: string;
}) => {
  const { t } = useTranslations();

  const [activeTab, setActiveTab] = useState(ALL_CATEGORIES_TAB);
  const tabsId = useId();
  const panelId = `${tabsId}-panel`;
  const activeTabId = activeTab === ALL_CATEGORIES_TAB ? undefined : Number(activeTab);
  const initialPage =
    activeTab === ALL_CATEGORIES_TAB ? (initialReferences?.meta.current_page ?? 1) : 1;

  const referencesQuery = useInfiniteQuery({
    queryKey: [CacheKey.REFERENCES, activeTab, locale, initialPage],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchReferences({
        locale,
        query: {
          page: pageParam,
          where: activeTabId ? { categories: { equals: activeTabId } } : undefined,
        },
        limit: 6,
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage.meta.current_page < lastPage.meta.last_page) {
        return lastPage.meta.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: initialPage,
    initialData:
      activeTab === ALL_CATEGORIES_TAB && initialReferences
        ? { pages: [initialReferences], pageParams: [initialPage] }
        : undefined,
    staleTime: 60_000,
  });

  const references = referencesQuery.data?.pages.flatMap(page => page.data) ?? [];
  const lastPage = referencesQuery.data?.pages.at(-1)?.meta.current_page;
  const nextPageHref =
    activeTab === ALL_CATEGORIES_TAB && listHref && lastPage
      ? `${listHref}?page=${lastPage + 1}`
      : undefined;

  return (
    <section
      className={cn(
        'custom-section',
        'gap-6 !pr-0 pb-6 pt-12 sm:gap-8 sm:pb-8 sm:pt-14 md:gap-10 md:pb-10 md:pt-16 lg:gap-12 lg:pb-12 lg:pt-24',
      )}
    >
      <div className={cn('custom-container', 'flex flex-col items-start justify-start gap-6')}>
        <Link
          prefetch={false}
          href={ROUTES.HOME()}
          className="text-sm text-primary transition-all hover:text-primary/80 sm:text-base"
        >
          {t('text160')}
        </Link>
        <h1 className="text-4xl font-medium tracking-tight sm:text-5xl">{t('text1091')}</h1>
      </div>
      <Tabs
        value={activeTab}
        defaultValue={activeTab}
        onValueChange={value => setActiveTab(value)}
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 sm:gap-8 md:gap-10 lg:gap-12',
        )}
      >
        <TabsList
          className={cn(
            'hide-scroll-bar',
            'flex h-fit w-full items-center justify-start gap-2 overflow-x-auto rounded-none bg-transparent p-0 sm:gap-4 lg:gap-6',
          )}
        >
          <TabsTrigger
            id={`${tabsId}-trigger-${ALL_CATEGORIES_TAB}`}
            aria-controls={panelId}
            value={ALL_CATEGORIES_TAB}
            className={cn(
              'ea-universal-tabs__tab-trigger',
              'flex h-10 items-center justify-center whitespace-nowrap rounded-[50px] px-6 text-sm transition-all sm:h-11 sm:text-base lg:h-[50px]',
            )}
          >
            {t('text2319')}
          </TabsTrigger>
          {categories.map(category => (
            <TabsTrigger
              id={`${tabsId}-trigger-${category.id}`}
              aria-controls={panelId}
              value={String(category.id)}
              key={category.id}
              className={cn(
                'ea-universal-tabs__tab-trigger',
                'flex h-10 items-center justify-center whitespace-nowrap rounded-[50px] px-6 text-sm transition-all sm:h-11 sm:text-base lg:h-[50px]',
              )}
            >
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent id={panelId} aria-labelledby={`${tabsId}-trigger-${activeTab}`} value={activeTab} className="w-full pr-6">
          {referencesQuery.error && <p className="pt-8 ">{t('text2364')}</p>}
          {referencesQuery.isSuccess && references.length === 0 && (
            <p className="w-full text-center font-medium">{t('text752')}</p>
          )}
          {/* {blogsQuery.isLoading && <p className="pt-8 ">Načítavam...</p>} */}
          {referencesQuery.isSuccess && references.length > 0 && (
            <div className="grid w-full grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3 xl:gap-6">
              {references.map(reference => (
                <ReferenceCard key={reference.id} reference={reference} headingTag="h2" />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      <Pagination
        query={referencesQuery}
        currentLength={references.length}
        nextPageHref={nextPageHref}
        previousPageHref={
          activeTab === ALL_CATEGORIES_TAB && listHref && initialPage > 1
            ? `${listHref}${initialPage > 2 ? `?page=${initialPage - 1}` : ''}`
            : undefined
        }
      />
    </section>
  );
};
